import { type ColDef } from 'ag-grid-community'
import { useMemo } from 'react'

import { type OrderListItem } from '@/api/admin-orders-types'
import { StatusCell, StatusColor } from '@/components/base/status-cell'
import { useI18n } from '@/i18n/i18n-react'

export interface UseOrderListColumnDefsOptions {
	includePoints?: boolean
}

export function useOrderListColumnDefs(options: UseOrderListColumnDefsOptions = {}): ColDef<OrderListItem>[] {
	const { L } = useI18n()
	const { includePoints = false } = options

	return useMemo<ColDef<OrderListItem>[]>(() => {
		const defs: ColDef<OrderListItem>[] = [
			{
				headerName: L.Orders.Column.Order(),
				field: 'displayId',
				minWidth: 200,
				flex: 2
			},
			{
				headerName: L.Orders.Column.Date(),
				field: 'date',
				type: 'date',
				minWidth: 140,
				flex: 2
			},
			{
				headerName: L.Orders.Column.SalesChannel(),
				field: 'salesChannel',
				minWidth: 100,
				flex: 2
			},
			{
				headerName: L.Orders.Column.PaymentStatus(),
				field: 'paymentStatus',
				minWidth: 100,
				flex: 1.5,
				cellRenderer: ({ value }) => (
					<StatusCell color={getPaymentStatusColor(value)}>{L.Enum.PaymentStatus(value)}</StatusCell>
				)
			},
			{
				headerName: L.Orders.Column.Total(),
				field: 'total',
				type: 'currency',
				minWidth: 80,
				flex: 1
			}
		]

		if (includePoints) {
			defs.push(
				{
					headerName: L.Orders.Column.PointsEarned(),
					field: 'pointsEarned',
					type: 'numericColumn',
					minWidth: 110,
					flex: 1,
					valueFormatter: ({ value }) => (typeof value === 'number' ? `+${value}` : '—')
				},
				{
					headerName: L.Orders.Column.PointsUsed(),
					field: 'pointsUsed',
					type: 'numericColumn',
					minWidth: 110,
					flex: 1,
					valueFormatter: ({ value }) => (typeof value === 'number' ? `−${value}` : '—')
				}
			)
		}

		return defs
	}, [L, includePoints])
}

export const getPaymentStatusColor = (status: string): StatusColor => {
	switch (status) {
		case 'PAID':
			return 'green'
		case 'PENDING':
		case 'PARTIALLY_PAID':
		case 'PARTIALLY_REFUNDED':
			return 'orange'
		case 'REFUNDED':
			return 'red'
		default:
			return 'grey'
	}
}
